/**
 * Export Employee Objectives Dialog
 *
 * Admin tool: pick employees who have objectives, download their objectives
 * (+ KPIs) as an Excel workbook in the SAME column format the Template Import
 * dialog accepts. Round-trip: export → edit template_name groupings in
 * Excel → re-import as templates via Actions > Import Templates from Excel.
 */
import { useState, useEffect, useMemo, useCallback } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { Badge } from '@/components/ui/badge'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Search, Download, Loader2, Users, FileSpreadsheet } from 'lucide-react'
import toast from 'react-hot-toast'
import { apiClient } from '@/services/apiClient'

interface ExportOwner {
  owner_user_id: number
  display_name: string
  employee_id: string | null
  position_type: string | null
  campus: string | null
  objective_count: number
  statuses: string[]
}

interface ExportEmployeeObjectivesDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export default function ExportEmployeeObjectivesDialog({
  open,
  onOpenChange,
}: ExportEmployeeObjectivesDialogProps) {
  const [owners, setOwners] = useState<ExportOwner[]>([])
  const [loading, setLoading] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState<Set<number>>(new Set())
  const [statusFilter, setStatusFilter] = useState<Set<string>>(new Set())

  // Load the owners list when the dialog opens
  useEffect(() => {
    if (!open) return
    let cancelled = false
    const load = async () => {
      setLoading(true)
      try {
        const response = await apiClient.get<{ owners: ExportOwner[]; total: number }>(
          '/api/v1/objectives/individual/export/owners'
        )
        if (!cancelled) {
          setOwners(response.data.owners || [])
        }
      } catch (err: any) {
        if (!cancelled) {
          toast.error(err?.response?.data?.detail || 'Failed to load team members with objectives')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [open])

  // Reset transient state on close
  useEffect(() => {
    if (!open) {
      setSearch('')
      setSelected(new Set())
      setStatusFilter(new Set())
    }
  }, [open])

  // Distinct statuses across all owners — drives the optional status filter chips
  const allStatuses = useMemo(() => {
    const s = new Set<string>()
    owners.forEach(o => o.statuses.forEach(st => s.add(st)))
    return Array.from(s).sort()
  }, [owners])

  const filteredOwners = useMemo(() => {
    let result = owners
    if (search.trim()) {
      const q = search.toLowerCase()
      result = result.filter(o =>
        o.display_name.toLowerCase().includes(q) ||
        (o.employee_id || '').toLowerCase().includes(q) ||
        (o.position_type || '').toLowerCase().includes(q) ||
        (o.campus || '').toLowerCase().includes(q)
      )
    }
    if (statusFilter.size > 0) {
      result = result.filter(o => o.statuses.some(st => statusFilter.has(st)))
    }
    return result
  }, [owners, search, statusFilter])

  const toggleOwner = (id: number, checked: boolean) => {
    setSelected(prev => {
      const next = new Set(prev)
      if (checked) next.add(id)
      else next.delete(id)
      return next
    })
  }

  const toggleAllFiltered = (checked: boolean) => {
    setSelected(prev => {
      const next = new Set(prev)
      filteredOwners.forEach(o => {
        if (checked) next.add(o.owner_user_id)
        else next.delete(o.owner_user_id)
      })
      return next
    })
  }

  const allFilteredSelected = filteredOwners.length > 0 &&
    filteredOwners.every(o => selected.has(o.owner_user_id))

  const toggleStatus = (status: string) => {
    setStatusFilter(prev => {
      const next = new Set(prev)
      if (next.has(status)) next.delete(status)
      else next.add(status)
      return next
    })
  }

  const handleExport = useCallback(async () => {
    if (selected.size === 0) return
    setExporting(true)
    try {
      const params = new URLSearchParams()
      params.set('owner_user_ids', Array.from(selected).join(','))
      if (statusFilter.size > 0) {
        params.set('statuses', Array.from(statusFilter).join(','))
      }
      const response = await apiClient.get<Blob>(
        `/api/v1/objectives/individual/export?${params.toString()}`,
        { responseType: 'blob' }
      )

      // Filename from Content-Disposition, with dated fallback
      const disposition = (response.headers?.['content-disposition'] as string) || ''
      const match = disposition.match(/filename="?([^"]+)"?/)
      const filename = match?.[1] ||
        `employee_objectives_export_${new Date().toISOString().slice(0, 10)}.xlsx`

      const url = window.URL.createObjectURL(response.data)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(url)

      toast.success(`Exported objectives for ${selected.size} team member${selected.size === 1 ? '' : 's'}`)
      onOpenChange(false)
    } catch (err: any) {
      // Blob error responses need decoding before we can show the detail
      let detail = 'Export failed'
      try {
        if (err?.response?.data instanceof Blob) {
          const text = await err.response.data.text()
          detail = JSON.parse(text)?.detail || detail
        } else {
          detail = err?.response?.data?.detail || err?.message || detail
        }
      } catch { /* keep generic */ }
      toast.error(detail)
    } finally {
      setExporting(false)
    }
  }, [selected, statusFilter, onOpenChange])

  const totalSelectedObjectives = useMemo(() => {
    return owners
      .filter(o => selected.has(o.owner_user_id))
      .reduce((sum, o) => sum + o.objective_count, 0)
  }, [owners, selected])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5 text-emerald-600" />
            Export Team Member Objectives
          </DialogTitle>
          <DialogDescription>
            Download selected team members&apos; objectives as an Excel file in the template-import
            format — edit the <code className="text-xs">template_name</code> column to group
            objectives, then re-import via &quot;Import Templates from Excel&quot;.
          </DialogDescription>
        </DialogHeader>

        {/* Search + status filter */}
        <div className="space-y-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search by name, team member number, role, or campus..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          {allStatuses.length > 1 && (
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-xs text-gray-500 mr-1">Objective status:</span>
              {allStatuses.map(status => (
                <Badge
                  key={status}
                  variant={statusFilter.has(status) ? 'default' : 'outline'}
                  className="cursor-pointer text-xs select-none"
                  onClick={() => toggleStatus(status)}
                >
                  {status}
                </Badge>
              ))}
              {statusFilter.size > 0 && (
                <button
                  className="text-xs text-gray-400 hover:text-gray-600 underline ml-1"
                  onClick={() => setStatusFilter(new Set())}
                >
                  clear
                </button>
              )}
            </div>
          )}
        </div>

        {/* Select-all row */}
        <div className="flex items-center justify-between px-1 py-1 border-b">
          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
            <Checkbox
              checked={allFilteredSelected}
              onCheckedChange={(c) => toggleAllFiltered(c === true)}
            />
            Select all {filteredOwners.length !== owners.length ? `${filteredOwners.length} filtered` : `${owners.length}`}
          </label>
          <span className="text-xs text-gray-400">
            {selected.size} team member{selected.size === 1 ? '' : 's'} · {totalSelectedObjectives} objective{totalSelectedObjectives === 1 ? '' : 's'} selected
          </span>
        </div>

        {/* Owner list */}
        <ScrollArea className="flex-1 min-h-[240px] max-h-[380px] pr-3">
          {loading ? (
            <div className="py-12 text-center text-gray-400">
              <Loader2 className="h-5 w-5 animate-spin inline mr-2" />
              Loading team members with objectives...
            </div>
          ) : filteredOwners.length === 0 ? (
            <div className="py-12 text-center text-gray-400 text-sm">
              <Users className="h-8 w-8 mx-auto mb-2 opacity-40" />
              {owners.length === 0
                ? 'No team members with objectives found.'
                : 'No team members match the current filters.'}
            </div>
          ) : (
            <div className="space-y-0.5">
              {filteredOwners.map(owner => (
                <label
                  key={owner.owner_user_id}
                  className="flex items-center gap-3 px-2 py-2 rounded hover:bg-gray-50 cursor-pointer"
                >
                  <Checkbox
                    checked={selected.has(owner.owner_user_id)}
                    onCheckedChange={(c) => toggleOwner(owner.owner_user_id, c === true)}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-900 truncate">
                      {owner.display_name}
                      {owner.employee_id && (
                        <span className="ml-2 font-mono text-xs text-gray-400">{owner.employee_id}</span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 truncate">
                      {[owner.position_type, owner.campus].filter(Boolean).join(' · ') || '—'}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    {owner.statuses.slice(0, 3).map(st => (
                      <Badge key={st} variant="outline" className="text-[10px] px-1.5 py-0 text-gray-500">
                        {st}
                      </Badge>
                    ))}
                    <Badge variant="secondary" className="text-xs">
                      {owner.objective_count}
                    </Badge>
                  </div>
                </label>
              ))}
            </div>
          )}
        </ScrollArea>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={exporting}>
            Cancel
          </Button>
          <Button
            onClick={handleExport}
            disabled={selected.size === 0 || exporting}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            {exporting ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Exporting...</>
            ) : (
              <><Download className="h-4 w-4 mr-2" /> Export {selected.size > 0 ? `(${selected.size})` : ''}</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
